import { Component } from 'react'
import * as cn from 'classnames'
import * as differenceBy from 'lodash/differenceBy'
import * as intersection from 'lodash/intersection'
import * as last from 'lodash/last'
import * as styles from 'components/features/insights/Insights/Insights.css'
import Insight from 'components/features/insights/Insight/Insight'
import { Insight as InsightInterface, InsightId, PlacementType } from 'interfaces/insights'
import { insightShownActionCreator, setActiveInsightActionCreator } from 'actions/insightsActions'
import Carousel from 'components/molecules/Carousel'

export type InsightsOwnProps = {
  placement: PlacementType
  isFullWidth?: boolean
}

export type InsightsStateProps = {
  insights: InsightInterface[]
  isMobile?: boolean
}

export type InsightsDispatchProps = {
  // null in case of fake insights
  onInsightShown: typeof insightShownActionCreator | null
  setActiveInsight?: typeof setActiveInsightActionCreator
}

type InsightsProps = InsightsStateProps & InsightsDispatchProps & InsightsOwnProps

type InsightsState = {
  insights: InsightsProps['insights']
  placement: InsightsProps['placement']
  previous: InsightInterface | null
  dismissed: InsightInterface | null
  order: InsightId[]
  activeIndex: number
}

const SLIDE_WIDTH_PERCENT = styles.insightWidth
const INSIGHTS_LIMIT_DESKTOP = 3

class Insights extends Component<InsightsProps, InsightsState> {
  static getDerivedStateFromProps (nextProps: InsightsProps, prevState: InsightsState) {
    const { insights: nextInsights, placement: nextPlacement } = nextProps
    const { insights: prevInsights, placement: prevPlacement, order: prevOrder } = prevState

    // detect dismissed insight
    // we use restInsights because nextInsights can containt totally new insights
    // and it can cause unnecessary dismiss animation
    const restInsights = intersection(nextInsights, prevInsights)
    const isDismissedByDismiss = restInsights.length !== 0 &&
      (prevInsights.length - restInsights.length) === 1
    const isDismissedByChangePlacement = prevPlacement !== nextPlacement &&
      prevInsights.length > 0 && nextInsights.length > 0
    const dismissed = isDismissedByDismiss || isDismissedByChangePlacement
      ? differenceBy(prevInsights, nextInsights, 'id')[0]
      : null

    const isUndismissed = nextInsights.length > prevInsights.length

    const isSkipped = nextInsights.length === prevInsights.length &&
      nextInsights.length > 1 &&
      last(nextInsights) === prevInsights[0]

    const previous = isSkipped
      ? prevInsights[0]
      : null

    let nextOrder = prevOrder

    if (isDismissedByDismiss && dismissed) {
      nextOrder = nextOrder.filter((id) => id !== dismissed.id)
    } else if (nextInsights.length !== prevInsights.length || restInsights.length !== prevInsights.length) {
      nextOrder = nextInsights.map((insight) => insight.id)
    }

    const nextActiveIndex = isUndismissed || !nextInsights.length
      ? 0
      : nextOrder.findIndex((id) => id === nextInsights[0].id)

    return {
      previous,
      dismissed,
      order: nextOrder,
      activeIndex: nextActiveIndex,

      insights: nextProps.insights,
      placement: nextProps.placement,
    }
  }

  state: InsightsState = {
    insights: this.props.insights,
    placement: this.props.placement,
    previous: null,
    dismissed: null,
    order: this.props.insights.map((insight) => insight.id),
    activeIndex: 0,
  }

  private get hasNext () {
    return this.props.insights.length > 1
  }

  render () {
    const {
      insights,
      isMobile,
    } = this.props
    const { previous, dismissed } = this.state

    if (!insights.length) {
      return null
    }

    const renderedInsights = isMobile
      ? this.renderMobile()
      : this.renderDesktop()
    let lastIndex = insights.length - 1

    return (
      <>
        { renderedInsights }
        { !isMobile && previous && (
          <div className={ cn(styles.insightsItem, styles.previous) }>
            { this.renderInsight(previous, lastIndex++) }
          </div>
        )}
        { dismissed && (
          <div className={ cn(styles.insightsItem, styles.dismissed) }>
            { this.renderInsight(dismissed, lastIndex++) }
          </div>
        )}
      </>
    )
  }

  renderInsight (insight: InsightInterface, index: number) {
    const { onInsightShown } = this.props
    const isActive = this.isActive(index)
    const onShown = isActive && onInsightShown ? onInsightShown : undefined

    return (
      <Insight
        key={ insight.id }
        insight={ insight }
        onShown={ onShown }
        hasNext={ this.hasNext }
        notTouchable={ !isActive }
      />
    )
  }

  renderMobile () {
    const { insights } = this.props
    const { activeIndex, order } = this.state
    const slideStyle = {
      width: insights.length > 1 ? SLIDE_WIDTH_PERCENT : '100%',
      height: '100%',
    }

    return (
      <Carousel
        activeIndex={ activeIndex }
        slideStyle={ slideStyle }
        onIndexChange={ this.changeOrder }
        className={ this.getInsightsClassname() }
      >
        { order.map((insightId, idx) => {
          const insightIndex = insights.findIndex(({ id }) => id === insightId)
          return this.renderInsight(insights[insightIndex], idx)
        })}
      </Carousel>
    )
  }

  renderDesktop () {
    const { insights } = this.props
    const insightsToRender = insights.slice(0, INSIGHTS_LIMIT_DESKTOP)

    return (
      <section className={ this.getInsightsClassname() } data-qa-marker='insight-item'>
        { insightsToRender.map((insight, idx) => (
          <div key={ insight.id } className={ styles.insightsItem }>
            { this.renderInsight(insight, idx) }
          </div>
        ))}
      </section>
    )
  }

  private getInsightsClassname () {
    const { isFullWidth } = this.props

    return cn(styles.insights, {
      [styles.fullWidth]: isFullWidth,
    })
  }

  private isActive (index: number) {
    const { isMobile } = this.props

    return isMobile
      ? index === this.state.activeIndex
      : index === 0
  }

  private changeOrder = (_activeIndex: number, realIndex: number) => {
    const { placement, setActiveInsight } = this.props
    const { order } = this.state

    if (!setActiveInsight) {
      return
    }

    const nextActiveId = order[realIndex]
    setActiveInsight({ placement, id: nextActiveId })
  }
}

export default Insights
